import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Device } from "mediasoup-client";
import { useMediasoupSignaling } from "./ThitdTest";

const SIGNALING_URL = process.env.REACT_APP_SIGNALING_URL || "wss://apps.pluglr.com/ws/stream";

const LiveRadioListener = () => {
  const [listenerName, setListenerName] = useState("");
  const [roomCode, setRoomCode] = useState("");
  const [joinedRoom, setJoinedRoom] = useState(false);
  const [status, setStatus] = useState("Idle");
  const [speakers, setSpeakers] = useState([]);
  const deviceRef = useRef(null);
  const recvTransportRef = useRef(null);
  const consumersRef = useRef(new Map());
  const signaling = useMediasoupSignaling(SIGNALING_URL, null);

  const safeRoomCode = useMemo(() => roomCode.trim(), [roomCode]);

  const updateStatus = useCallback((message) => {
    setStatus(message);
  }, []);

  const ensureDevice = useCallback(async () => {
    if (deviceRef.current) {
      return deviceRef.current;
    }
    const { routerRtpCapabilities } = await signaling.request("getRouterRtpCapabilities", { roomId: safeRoomCode });
    const device = new Device();
    await device.load({ routerRtpCapabilities });
    deviceRef.current = device;
    return device;
  }, [safeRoomCode, signaling]);

  const connectTransport = useCallback(
    async (transportId, dtlsParameters) => {
      await signaling.request("connectTransport", {
        roomId: safeRoomCode,
        transportId,
        dtlsParameters,
      });
    },
    [safeRoomCode, signaling]
  );

  const ensureRecvTransport = useCallback(async () => {
    if (recvTransportRef.current) {
      return recvTransportRef.current;
    }
    const device = await ensureDevice();
    const transportOptions = await signaling.request("createTransport", {
      roomId: safeRoomCode,
      direction: "receive",
    });
    const transport = device.createRecvTransport(transportOptions);
    transport.on("connect", async ({ dtlsParameters }, success, fail) => {
      try {
        await connectTransport(transportOptions.id, dtlsParameters);
        success();
      } catch (error) {
        fail(error);
      }
    });
    transport.on("connectionstatechange", (state) => {
      if (state === "connected") {
        updateStatus("Receiving audio");
      }
      if (state === "failed" || state === "closed") {
        updateStatus("Transport closed");
      }
    });
    recvTransportRef.current = transport;
    return transport;
  }, [connectTransport, ensureDevice, safeRoomCode, signaling, updateStatus]);

  const consumeSpeaker = useCallback(
    async ({ producerId, speakerId }) => {
      const device = await ensureDevice();
      const transport = await ensureRecvTransport();
      const { id, kind, rtpParameters } = await signaling.request("consume", {
        roomId: safeRoomCode,
        producerId,
        transportId: transport.id,
        rtpCapabilities: device.rtpCapabilities,
      });
      if (kind !== "audio") {
        await signaling.request("resume", { roomId: safeRoomCode, consumerId: id });
        return;
      }
      const consumer = await transport.consume({ id, producerId, kind, rtpParameters });
      const stream = new MediaStream([consumer.track]);
      consumersRef.current.set(id, { consumer, speakerId });
      setSpeakers((prev) => {
        const remaining = prev.filter((item) => item.consumerId !== id);
        return [...remaining, { consumerId: id, speakerId, stream }];
      });
      await signaling.request("resume", { roomId: safeRoomCode, consumerId: id });
    },
    [ensureDevice, ensureRecvTransport, safeRoomCode, signaling]
  );

  const requestExistingSpeakers = useCallback(async () => {
    if (!joinedRoom) {
      return;
    }
    const list = await signaling.request("listProducers", { roomId: safeRoomCode });
    if (Array.isArray(list)) {
      for (const item of list) {
        await consumeSpeaker({ producerId: item.producerId || item.id, speakerId: item.speakerId || item.id });
      }
    }
  }, [consumeSpeaker, joinedRoom, safeRoomCode, signaling]);

  const removeSpeaker = useCallback((target) => {
    const entry = Array.from(consumersRef.current.entries()).find(([, value]) => value.speakerId === target);
    if (!entry) {
      return;
    }
    const [consumerId, { consumer }] = entry;
    consumer.close();
    consumersRef.current.delete(consumerId);
    setSpeakers((prev) => prev.filter((item) => item.consumerId !== consumerId));
  }, []);

  const leaveRoom = useCallback(() => {
    consumersRef.current.forEach(({ consumer }) => consumer.close());
    consumersRef.current.clear();
    setSpeakers([]);
    if (recvTransportRef.current) {
      recvTransportRef.current.close();
      recvTransportRef.current = null;
    }
    if (deviceRef.current) {
      deviceRef.current = null;
    }
    setJoinedRoom(false);
    updateStatus("Idle");
  }, [updateStatus]);

  const handleServerNotification = useCallback(
    async (message) => {
      if (!message) {
        return;
      }
      if (message.type === "room_ready") {
        if (message.roomId) {
          setRoomCode(message.roomId);
        }
        setJoinedRoom(true);
        updateStatus("Room ready");
        await requestExistingSpeakers();
        return;
      }
      if (message.type === "producer_available") {
        await consumeSpeaker({ producerId: message.producerId, speakerId: message.speakerId || message.producerId });
        return;
      }
      if (message.type === "producer_closed") {
        removeSpeaker(message.speakerId || message.producerId);
        return;
      }
      if (message.type === "room_closed") {
        leaveRoom();
        return;
      }
    },
    [consumeSpeaker, leaveRoom, removeSpeaker, requestExistingSpeakers, updateStatus]
  );

  useEffect(() => {
    signaling.setHandler(handleServerNotification);
    return () => {
      signaling.setHandler(null);
    };
  }, [handleServerNotification, signaling]);

  useEffect(() => {
    return () => {
      leaveRoom();
    };
  }, [leaveRoom]);

  const joinRoom = useCallback(async () => {
    if (!safeRoomCode || !signaling.ready) {
      return;
    }
    updateStatus("Joining room");
    await signaling.request("listenerReady", {
      roomId: safeRoomCode,
      name: listenerName.trim() || undefined,
    });
    signaling.sendRaw({ type: "join", role: "listener", roomId: safeRoomCode, name: listenerName.trim() || undefined });
    setJoinedRoom(true);
    await requestExistingSpeakers();
  }, [listenerName, requestExistingSpeakers, safeRoomCode, signaling]);

  const disconnect = useCallback(async () => {
    if (safeRoomCode) {
      signaling.sendRaw({ type: "leave", roomId: safeRoomCode });
    }
    leaveRoom();
  }, [leaveRoom, safeRoomCode, signaling]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "24px" }}>
      <h1>Pluglr Live Radio Listener</h1>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span>Listener name</span>
          <input value={listenerName} onChange={(event) => setListenerName(event.target.value)} disabled={joinedRoom} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span>Room code</span>
          <input value={roomCode} onChange={(event) => setRoomCode(event.target.value)} disabled={joinedRoom} />
        </label>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button onClick={joinRoom} disabled={joinedRoom || !safeRoomCode || !signaling.ready}>
            Join room
          </button>
          <span>Status: {status}</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <h2>Speakers</h2>
        {speakers.length === 0 ? <span>No speakers available</span> : null}
        {speakers.map((speaker) => (
          <div key={speaker.consumerId} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span>Speaker {speaker.speakerId || speaker.consumerId}</span>
            <audio
              controls
              autoPlay
              ref={(element) => {
                if (element && element.srcObject !== speaker.stream) {
                  element.srcObject = speaker.stream;
                }
              }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: "12px" }}>
        <button onClick={disconnect} disabled={!joinedRoom}>
          Leave room
        </button>
      </div>
    </div>
  );
};

export default LiveRadioListener;
